import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getStoredLang,
  translations,
  type Lang,
  type TranslationKey,
} from "./translations";

const STORAGE_KEY = "avg.lang";
const DEFAULT_LANG: Lang = "sv";

type Vars = Record<string, string | number>;

export type TranslateFn = (key: TranslationKey, vars?: Vars) => string;

interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: TranslateFn;
  /** Browser locale string suitable for Date.toLocaleString. */
  locale: string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function format(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const v = vars[name];
    return v === undefined ? `{${name}}` : String(v);
  });
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getStoredLang);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore storage errors
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((l: Lang) => setLangState(l), []);

  const t = useCallback<TranslateFn>(
    (key, vars) => {
      const dict = translations[lang];
      const template = dict[key] ?? translations[DEFAULT_LANG][key] ?? key;
      return format(template, vars);
    },
    [lang],
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      lang,
      setLang,
      t,
      locale: lang === "sv" ? "sv-SE" : "en-GB",
    }),
    [lang, setLang, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}

export function useT(): TranslateFn {
  return useI18n().t;
}

/**
 * Localized month label. Replaces `monthLabel` from `@avg/shared` in the UI.
 * The shared helper is still used by code that intentionally needs the
 * Swedish form (e.g. backend / Excel export).
 */
export function useMonthLabel() {
  const t = useT();
  return (month: number) =>
    t(`months.${month}` as TranslationKey, undefined) || String(month);
}

/**
 * Localized wage-code description. Falls back to the description from the
 * database (in whatever language was entered) when the code is custom.
 */
export function useWageCodeLabel() {
  const t = useT();
  const { lang } = useI18n();
  return (code: string, fallback: string) => {
    const key = `wageCode.${code}` as TranslationKey;
    const dict = translations[lang];
    if (key in dict) return dict[key];
    return fallback;
  };
}
